import { paperScript, scriptProblem } from "./paperScript.js";
import { checkArithmetic, optionIndex, parseNumericAnswer, type CurriculumStandard } from "./exam.js";
import { expectedDifficultyCounts, type Difficulty, type ValidationIssue } from "./uasa.js";
import { isIcon } from "./pdf/icons.js";

/**
 * UASA-style papers ("Set Soalan Latihan") for every primary subject, Tahun 1–6.
 *
 * Section layouts follow the school sample sets the owner supplied (Oct 2026) and are used as blueprints;
 * subjects without a sample get a general paper of the same shape. They are NOT verified
 * against an official KPM format document, so every paper is labelled "Latihan".
 * Questions are tied to imported DSKP standards; diagrams are structured data drawn by our renderer.
 */

// ── Diagrams the renderer can draw ─────────────────────────────────────────
export type Diagram =
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "bar"; xLabel?: string; yLabel?: string; bars: { label: string; value: number }[] }
  | { type: "clock"; hour: number; minute: number }
  | { type: "numberline"; from: number; to: number; step: number; marks?: { value: number; label: string }[] }
  | { type: "fraction"; parts: number; shaded: number; shape?: "bar" | "circle" }
  | { type: "shapes"; shapes: { points: [number, number][]; label?: string }[]; labels?: { x: number; y: number; text: string }[] }
  | { type: "box"; textMs: string; textEn?: string }
  | { type: "picture"; items: PictureItem[]; cols?: number; size?: "s" | "m" | "l" }
  | { type: "scene"; prompt: string; imageId?: string; icon?: string }
  | { type: "frame"; height: number; grid?: boolean; hint?: string };

/**
 * One drawn picture in a `picture` diagram. `icon` is a name from the renderer's icon set (or "colour:merah",
 * "line:zigzag", "tex:kasar"). `ask` prints an empty answer box under the picture instead of its label, so
 * the same diagram works as "Labelkan gambar" / "Namakan alatan"; `label` then holds the answer.
 */
export interface PictureItem {
  /** Vector icon name; the fallback when `prompt` has no generated image. Optional when `prompt` is given. */
  icon?: string;
  /** What to draw (pencil-sketch illustration generated per paper). */
  prompt?: string;
  /** Set by the generator once the image exists (key under the paper's images). */
  imageId?: string;
  label?: string; tag?: string; ask?: boolean; count?: number;
}

export type DiagramKind = Diagram["type"];
export const DIAGRAM_KINDS: readonly DiagramKind[] = ["table", "bar", "clock", "numberline", "fraction", "shapes", "box", "picture", "scene", "frame"];

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const str = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";


/** Things we never ask an image model to draw: depictions of Allah, prophets and angels, and real people. */
const FORBIDDEN_IMAGE = /\b(allah|nabi|rasul|malaikat|prophet|angel|muhammad|jibril|tuhan|god)\b/i;
/** Returns a reason when an image prompt is unusable; undefined prompts are fine unless `required`. */
export function promptProblem(p: unknown, required = false): string | null {
  if (p === undefined || p === null || p === "") return required ? "gambar: 'prompt' diperlukan" : null;
  if (typeof p !== "string" || p.trim().length < 3 || p.length > 300) return "gambar: prompt 3–300 aksara";
  if (FORBIDDEN_IMAGE.test(p)) return "gambar: jangan gambarkan Allah, nabi atau malaikat; pilih adegan murid belajar/beribadah tanpa tokoh tersebut";
  return null;
}

/** Returns a reason when the diagram cannot be drawn, null when it is fine. */
export function diagramProblem(d: unknown): string | null {
  if (!d || typeof d !== "object") return "rajah bukan objek";
  const x = d as Record<string, unknown>;
  switch (x.type) {
    case "table": {
      const h = x.headers, r = x.rows;
      if (!Array.isArray(h) || h.length < 1 || h.length > 7 || !h.every((c) => typeof c === "string")) return "jadual: headers 1–7 lajur";
      if (!Array.isArray(r) || r.length < 1 || r.length > 10) return "jadual: rows 1–10 baris";
      if (!r.every((row) => Array.isArray(row) && row.length === h.length)) return "jadual: setiap baris mesti sama bilangan lajur dengan headers";
      return null;
    }
    case "bar": {
      const b = x.bars;
      if (!Array.isArray(b) || b.length < 2 || b.length > 8) return "carta palang: 2–8 palang";
      if (!b.every((i) => i && str((i as { label: unknown }).label) && num((i as { value: unknown }).value) && (i as { value: number }).value >= 0)) return "carta palang: label dan value (≥0)";
      return null;
    }
    case "clock":
      return Number.isInteger(x.hour) && (x.hour as number) >= 1 && (x.hour as number) <= 12 && Number.isInteger(x.minute) && (x.minute as number) >= 0 && (x.minute as number) < 60
        ? null : "jam: hour 1–12, minute 0–59";
    case "numberline": {
      if (!num(x.from) || !num(x.to) || !num(x.step) || (x.step as number) <= 0 || (x.to as number) <= (x.from as number)) return "garis nombor: from < to, step > 0";
      const ticks = ((x.to as number) - (x.from as number)) / (x.step as number);
      return ticks >= 2 && ticks <= 20 ? null : "garis nombor: 2–20 sela";
    }
    case "fraction":
      return Number.isInteger(x.parts) && (x.parts as number) >= 2 && (x.parts as number) <= 12 && Number.isInteger(x.shaded) && (x.shaded as number) >= 0 && (x.shaded as number) <= (x.parts as number)
        ? null : "pecahan: parts 2–12, shaded 0..parts";
    case "shapes": {
      const s = x.shapes;
      if (!Array.isArray(s) || s.length < 1 || s.length > 4) return "bentuk: 1–4 bentuk";
      for (const sh of s as { points?: unknown }[]) {
        const p = sh?.points;
        if (!Array.isArray(p) || p.length < 3 || p.length > 8) return "bentuk: 3–8 bucu";
        if (!p.every((pt) => Array.isArray(pt) && pt.length === 2 && num(pt[0]) && num(pt[1]) && pt[0] >= 0 && pt[1] >= 0 && pt[0] <= 20 && pt[1] <= 12)) return "bentuk: koordinat grid x 0–20, y 0–12";
      }
      return null;
    }
    case "box":
      return str(x.textMs) ? null : "kotak: textMs";
    case "picture": {
      const it = x.items;
      if (!Array.isArray(it) || it.length < 1 || it.length > 8) return "gambar: 1–8 item";
      for (const p of it as Record<string, unknown>[]) {
        if (!p) return "gambar: item kosong";
        if (p.icon !== undefined && !isIcon(p.icon)) return `gambar: ikon tidak dikenali '${String(p.icon)}'`;
        if (p.icon === undefined && p.prompt === undefined) return "gambar: perlu icon atau prompt";
        const pp = promptProblem(p.prompt);
        if (pp) return pp;
        if (p.label !== undefined && typeof p.label !== "string") return "gambar: label mesti teks";
        if (p.ask === true && !str(p.label)) return "gambar: item ask perlu label (jawapan)";
        if (p.count !== undefined && !(Number.isInteger(p.count) && (p.count as number) >= 1 && (p.count as number) <= 10)) return "gambar: count 1–10";
      }
      if (x.cols !== undefined && !(Number.isInteger(x.cols) && (x.cols as number) >= 1 && (x.cols as number) <= 5)) return "gambar: cols 1–5";
      return null;
    }
    case "scene":
      return promptProblem(x.prompt, true) ?? (x.icon !== undefined && !isIcon(x.icon) ? "adegan: ikon tidak dikenali" : null);
    case "frame":
      return num(x.height) && (x.height as number) >= 50 && (x.height as number) <= 260 ? null : "ruang lukisan: height 50–260";
    default:
      return `jenis rajah tidak disokong: ${String(x.type)}`;
  }
}

// ── Formats ─────────────────────────────────────────────────────────────────
/**
 * objektif  4 options A–D
 * jawapan   short answer with one final answer (Matematik T4–6 Bahagian A)
 * struktur  context + parts (a), (b)… (optionally on a reading passage)
 * susun     word tiles to arrange into one sentence ("Susun perkataan menjadi ayat")
 * isian     a passage with numbered/lettered blanks, optionally a word bank, a poster or picture-frame outline
 * padan     matching: pictures/words in two columns, the pupil joins pairs (1 mark per pair)
 * tanda     statements (words and/or pictures) each ticked (✓) or crossed (×) (1 mark per statement)
 */
export type QuestionKind = "objektif" | "jawapan" | "struktur" | "susun" | "isian" | "padan" | "tanda";

export interface FormatItem {
  /** New papers require an actual generated illustration in this slot. */
  visual?: boolean;
  kind: QuestionKind;
  marks: number;
  /** struktur: marks of each part. */
  parts?: number[];
  /** isian: number of blanks and how they are labelled. */
  blanks?: number;
  blankStyle?: "num" | "alpha";
  /** isian: the paper prints a word bank to choose from. */
  wordBank?: boolean;
  /** isian: stimulus shown above the passage — a poster (title + points) or 2–4 framed outline panels. */
  stimulus?: "poster" | "panels";
  /** struktur: questions are on a reading passage printed with the question. */
  passage?: boolean;
  /** objektif: the four options are pictures (optionIcons) rather than words. */
  pictureOptions?: boolean;
  /** What this item looks like in the sample sets; passed to the AI as guidance. */
  hint?: string;
}

export type SectionId = "A" | "B" | "C" | "D" | "E" | "F" | "G";

export interface FormatSection {
  id: SectionId;
  items: FormatItem[];
  /** Suggested minutes (printed under the section heading). */
  minutes?: number;
  /** "Jawab satu soalan sahaja": the section holds alternatives; only one item's marks count. */
  chooseOne?: boolean;
}

export interface UasaFormat {
  id: string;
  subject: string;
  /** Kertas 1 / Kertas 2 (language subjects have two papers). */
  paper: 1 | 2;
  paperName?: string;
  code: string;
  years: readonly number[];
  totalMarks: number;
  durationMinutes: number;
  /** BM stems with English italics (DLP subjects in Tahun 4–6). */
  bilingual: boolean;
  /** Language of the questions themselves ("en" for Bahasa Inggeris). */
  language: "ms" | "en";
  /** 1 = Tahun 1–3 layout (SULIT cover, no examiner column); 2 = Tahun 4–6 layout. */
  tahap: 1 | 2;
  /** Question numbers run on across sections (BM Kertas 1: Soalan 21 in Bahagian B). */
  continuousNumbering: boolean;
  /** Only standards whose SP code starts with one of these (e.g. reading skills for Kertas 1). */
  spPrefixes?: readonly string[];
  difficultyRatio: [number, number, number];
  sections: FormatSection[];
  /** true only for the official 2025 formats checked against a KPM sample. */
  verified: boolean;
}

const many = (n: number, item: FormatItem): FormatItem[] => Array.from({ length: n }, () => ({ ...item }));

/** Old stored blueprints (before items existed) had { kind, marks[], partSplit }. */
export function sectionItems(sec: FormatSection | (Omit<FormatSection, "items"> & { kind?: QuestionKind; marks?: number[]; partSplit?: Record<number, number[]>; items?: FormatItem[] })): FormatItem[] {
  if (Array.isArray((sec as FormatSection).items)) return (sec as FormatSection).items;
  const old = sec as { kind?: QuestionKind; marks?: number[]; partSplit?: Record<number, number[]> };
  return (old.marks ?? []).map((m) => ({ kind: old.kind ?? "jawapan", marks: m, ...(old.kind === "struktur" ? { parts: old.partSplit?.[m] ?? [m] } : {}) }));
}

/** Marks a section contributes to the total (one alternative when the pupil chooses one). */
export const sectionMarks = (sec: FormatSection): number => {
  const items = sectionItems(sec);
  return sec.chooseOne ? Math.max(0, ...items.map((i) => i.marks)) : items.reduce((a, i) => a + i.marks, 0);
};

const T13 = [1, 2, 3] as const;
const T46 = [4, 5, 6] as const;
const OBJ_PIC = "Objektif. Jika sampel guna gambar, tulis situasi ringkas (atau letak kotak 'box') kerana sistem tidak melukis gambar.";

const SUSUN_PARTS = (lang: "ms" | "en"): FormatItem => ({
  kind: "struktur", marks: 4, parts: [1, 1, 1, 1],
  hint: lang === "ms"
    ? "Soalan susun perkataan: stemMs 'Susun perkataan di bawah supaya menjadi ayat yang betul.'; setiap bahagian (a)–(d) stemMs = perkataan bercampur dipisah ' / ' (cth 'Ipoh / Ibu / Perak / negeri / ialah'); answer = ayat lengkap."
    : "Rearrange the words: stemMs 'Rearrange the words to form a correct sentence.'; each part (a)–(d) stemMs = jumbled words separated by ' / '; answer = the full sentence.",
});
const CLOZE_BANK = (lang: "ms" | "en"): FormatItem => ({
  kind: "isian", marks: 6, blanks: 6, blankStyle: "alpha", wordBank: true,
  hint: lang === "ms"
    ? "Petikan pendek (60–90 patah perkataan) dengan 6 tempat kosong bertanda (a)…(f); wordBank 6–8 perkataan (termasuk semua jawapan); stemMs 'Pilih perkataan yang betul untuk diisi pada tempat kosong dalam petikan di bawah.'"
    : "Short passage (60–90 words) with 6 blanks marked (a)…(f); wordBank of 6–8 words containing every answer; stemMs 'Choose the correct word to fill in the blanks in the passage.'",
});
const COMPREHENSION = (lang: "ms" | "en"): FormatItem => ({
  kind: "struktur", marks: 10, parts: [2, 2, 3, 3], passage: true,
  hint: lang === "ms"
    ? "Kefahaman: 'passage' petikan asli 100–150 patah perkataan (cerita/berita/surat sesuai umur); stemMs 'Baca petikan di bawah dengan teliti, kemudian jawab soalan-soalan yang berikutnya.'; bahagian (a)–(d) soalan kefahaman (literal ke inferens)."
    : "Comprehension: 'passage' original 100–150 words (story/notice/letter for the age); stemMs 'Read the passage below and answer the questions that follow.'; parts (a)–(d) from literal to inferential.",
});
const SUSUN_AYAT = (lang: "ms" | "en"): FormatItem => ({
  kind: "susun", marks: 2,
  hint: lang === "ms"
    ? "Susun perkataan menjadi ayat: 'words' 4–6 jubin perkataan/frasa dalam susunan bercampur; answer = ayat lengkap yang menggunakan SEMUA jubin tepat sekali. stemMs boleh kosong."
    : "Word tiles: 'words' 4–6 tiles in jumbled order; answer = the full sentence using EVERY tile exactly once. stemMs may be empty.",
});
const ULASAN = (lang: "ms" | "en"): FormatItem => ({
  kind: "isian", marks: 15, blanks: 5, blankStyle: "num", stimulus: "poster",
  hint: lang === "ms"
    ? "Ulasan berdasarkan bahan rangsangan: 'posterTitle' tajuk poster (cth 'KEBAIKAN AKTIVITI SENAMAN') dan 'kerangka' [[4–6 isi ringkas]]; 'passage' ulasan 80–120 patah perkataan dengan 5 tempat kosong (1)…(5) yang diisi dengan ayat/frasa berdasarkan poster; stemMs 'Lihat bahan rangsangan di bawah dengan teliti. Lengkapkan ulasan berdasarkan bahan rangsangan.'"
    : "Notes from a stimulus: 'posterTitle' and 'kerangka' [[4–6 short points]]; 'passage' 80–120 words with 5 blanks (1)…(5) completed from the poster; stemMs 'Look at the poster below. Complete the text based on the poster.'",
});
const KARANGAN = (lang: "ms" | "en"): FormatItem => ({
  kind: "isian", marks: 25, blanks: 10, blankStyle: "num", stimulus: "panels",
  hint: lang === "ms"
    ? "Karangan berpandu: stemMs 'Lengkapkan karangan yang bertajuk \"…\" berdasarkan kerangka yang diberi.'; 'kerangka' 4 panel, setiap panel 2–3 isi ringkas (ganti gambar); 'passage' karangan 120–180 patah perkataan dengan 10 tempat kosong (1)…(10) yang diisi daripada kerangka."
    : "Guided writing: stemMs 'Complete the composition titled \"…\" using the outline given.'; 'kerangka' 4 panels of 2–3 short notes each (instead of pictures); 'passage' 120–180 words with 10 blanks (1)…(10) filled from the outline.",
});

const langPaper1 = (subject: string, lang: "ms" | "en", code: string, prefixes: string[], years: readonly number[] = T13): UasaFormat => ({
  id: `${subject}-k1-t${years[0]}${years[years.length - 1]}-sample-2026`, subject, paper: 1, paperName: lang === "ms" ? "Kertas 1 - Pemahaman" : "Kertas 1 - Pemahaman (Paper 1 - Comprehension)",
  code, years, totalMarks: 50, durationMinutes: 75, bilingual: false, language: lang, tahap: years[0]! >= 4 ? 2 : 1, continuousNumbering: true,
  spPrefixes: prefixes, difficultyRatio: [5, 3, 2], verified: false,
  sections: [
    { id: "A", items: many(20, { kind: "objektif", marks: 1, hint: lang === "ms" ? `${OBJ_PIC} Kebanyakan 'Isi tempat kosong dengan jawapan yang paling sesuai' (kosa kata, kata nama/kerja/sifat, imbuhan, kata hubung, sinonim, antonim, simpulan bahasa, peribahasa mudah).` : `${OBJ_PIC} Mostly 'Choose the best answer to complete the sentence' (vocabulary, grammar, spelling, punctuation) and a short reading text.` }) },
    { id: "B", items: [SUSUN_PARTS(lang), CLOZE_BANK(lang), COMPREHENSION(lang), COMPREHENSION(lang)] },
  ],
});
const langPaper2 = (subject: string, lang: "ms" | "en", code: string, prefixes: string[], years: readonly number[] = T13): UasaFormat => ({
  id: `${subject}-k2-t${years[0]}${years[years.length - 1]}-sample-2026`, subject, paper: 2, paperName: lang === "ms" ? "Kertas 2 - Penulisan" : "Kertas 2 - Penulisan (Paper 2 - Writing)",
  code, years, totalMarks: 50, durationMinutes: 75, bilingual: false, language: lang, tahap: years[0]! >= 4 ? 2 : 1, continuousNumbering: false,
  spPrefixes: prefixes, difficultyRatio: [5, 3, 2], verified: false,
  sections: [
    { id: "A", minutes: 15, items: many(5, SUSUN_AYAT(lang)) },
    { id: "B", minutes: 25, items: [ULASAN(lang)] },
    { id: "C", minutes: 35, chooseOne: true, items: [KARANGAN(lang), KARANGAN(lang)] },
  ],
});

const struktur = (marks: number, parts: number[], hint: string, extra: Partial<FormatItem> = {}): FormatItem => ({ kind: "struktur", marks, parts, hint, ...extra });
const objektif = (hint: string): FormatItem => ({ kind: "objektif", marks: 1, hint });

/** Bahasa Melayu Tahun 4–6, official 2025: A objektif 10m, B respons 10m, C penulisan perenggan 10m, D karangan 20m. */
const BM_T46: UasaFormat = {
  id: "bahasa_melayu-t46-uasa-2025", subject: "bahasa_melayu", paper: 1, paperName: "Pemahaman dan Penulisan", code: "011",
  years: T46, totalMarks: 50, durationMinutes: 75, bilingual: false, language: "ms", tahap: 2, continuousNumbering: true,
  spPrefixes: ["2.", "3.", "5."], difficultyRatio: [5, 3, 2], verified: true,
  sections: [
    { id: "A", minutes: 15, items: [
      ...many(4, objektif("Tatabahasa: 'Pilih jawapan yang paling sesuai untuk melengkapkan ayat' (imbuhan, kata hubung, kata sendi nama, penjodoh bilangan, simpulan bahasa, peribahasa).")),
      ...many(6, objektif("Kefahaman petikan: soalan berdasarkan petikan atau teks grafik ringkas (iklan, jadual, poster) yang dicetak dalam 'passage' soalan pertama kumpulan; literal hingga inferens.")),
    ] },
    { id: "B", minutes: 15, items: [
      struktur(3, [1, 1, 1], "Tatabahasa (respons terhad): betulkan kesalahan, isi imbuhan atau bina ayat pendek; satu jawapan tepat bagi setiap bahagian."),
      struktur(4, [1, 1, 2], "Kefahaman (respons terhad): 'passage' petikan 100–150 patah perkataan; soalan (a)–(c) literal ke inferens.", { passage: true }),
      struktur(3, [3], "Respons kritis dan kreatif: berikan pendapat atau cadangan berdasarkan situasi; skema senaraikan 3 idea yang diterima."),
    ] },
    { id: "C", minutes: 15, items: [struktur(10, [10], "Penulisan perenggan (respons terbuka): tulis SATU perenggan 60–80 patah perkataan berdasarkan bahan rangsangan (gambar/situasi/kerangka ringkas). Jawapan = contoh perenggan; skema: isi, huraian, bahasa.")] },
    { id: "D", minutes: 30, items: [struktur(20, [20], "Karangan (respons terbuka): tulis karangan 120–150 patah perkataan bertajuk yang diberi (berbentuk cerita, pendapat atau laporan) dengan kerangka 4–5 isi. Jawapan = contoh karangan; skema: isi, huraian, susunan, bahasa.")] },
  ],
};

/** English Year 4–6, official 2025: Parts 1–7 (A–G here), CEFR A1–A2. */
const BI_T46: UasaFormat = {
  id: "bahasa_inggeris-t46-uasa-2025", subject: "bahasa_inggeris", paper: 1, paperName: "Reading and Writing", code: "013",
  years: T46, totalMarks: 50, durationMinutes: 75, bilingual: false, language: "en", tahap: 2, continuousNumbering: true,
  spPrefixes: ["3.", "4.", "5."], difficultyRatio: [5, 3, 2], verified: true,
  sections: [
    { id: "A", items: many(5, objektif("Part 1 – Short texts: a notice, sign, message or label printed in 'passage'; choose what it means.")) },
    { id: "B", items: many(4, objektif("Part 2 – Lexico-grammar: choose the correct word to complete the sentence (no pictures).")) },
    { id: "C", items: many(5, objektif("Part 3 – Linear or non-linear text (short article, timetable, chart) printed in 'passage' of the first question; comprehension questions.")) },
    { id: "D", items: [struktur(6, [1, 1, 1, 1, 1, 1], "Part 4 – Matching multiple texts: six short descriptions (a)–(f) to be matched with the correct person/place/item from a list in 'passage'.", { passage: true })] },
    { id: "E", items: [struktur(5, [1, 1, 1, 1, 1], "Part 5 – Word completion: five sentences, each with one word missing letters (e.g. 'b _ _ k'); answer = the full word.")] },
    { id: "F", items: [struktur(10, [10], "Part 6 – Guided writing: a short communicative message (email, note, postcard) of 40–60 words from given prompts. Answer = a model message; scheme: content, communicative achievement, language.")] },
    { id: "G", items: [struktur(15, [15], "Part 7 – Notes expansion: expand 5–6 given notes into a longer text of 80–100 words. Answer = a model text; scheme: content, organisation, language.")] },
  ],
};

const contentT13 = (subject: "sains" | "matematik", code: string): UasaFormat => ({
  id: `${subject}-t13-sample-2026`, subject, paper: 1, paperName: "Kertas 1", code, years: T13, totalMarks: 40, durationMinutes: 60,
  bilingual: false, language: "ms", tahap: 1, continuousNumbering: false, difficultyRatio: [5, 3, 2], verified: false,
  sections: [
    { id: "A", items: many(20, { kind: "objektif", marks: 1, hint: subject === "matematik" ? "Objektif. Guna rajah (garis nombor, jam, pecahan, bentuk, jadual) bila sesuai; nombor mesti tepat." : OBJ_PIC }) },
    { id: "B", items: many(4, { kind: "struktur", marks: 5, parts: [2, 3], hint: subject === "matematik" ? "Struktur ringkas: (a) dan (b) pengiraan/padanan; sertakan check untuk jawapan nombor." : "Struktur ringkas: padanan, tanda (/), lengkapkan jadual atau pemerhatian; boleh guna jadual." }) },
  ],
});

export const UASA_FORMATS: Readonly<Record<string, UasaFormat>> = {
  sains: {
    id: "sains-uasa-sample-2026", subject: "sains", paper: 1, code: "018", years: T46, totalMarks: 50, durationMinutes: 75, bilingual: true,
    language: "ms", tahap: 2, continuousNumbering: false, difficultyRatio: [5, 3, 2], verified: false,
    sections: [
      { id: "A", items: many(10, { kind: "objektif", marks: 1 }) },
      { id: "B", items: many(2, { kind: "struktur", marks: 4, parts: [2, 2] }) },
      { id: "C", items: many(4, { kind: "struktur", marks: 8, parts: [1, 2, 1, 2, 2], hint: "Kemahiran proses sains berdasarkan penyiasatan, biasanya dengan JADUAL data." }) },
    ],
  },
  matematik: {
    id: "matematik-uasa-sample-2026", subject: "matematik", paper: 1, code: "015", years: T46, totalMarks: 50, durationMinutes: 75, bilingual: true,
    language: "ms", tahap: 2, continuousNumbering: false, difficultyRatio: [5, 3, 2], verified: false,
    sections: [
      { id: "A", minutes: 45, items: [1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3].map((m, i) => ({ kind: "jawapan" as const, marks: m, ...(i % 2 === 0 ? { hint: "Soalan ini WAJIB ada 'diagram' vektor (bar/clock/numberline/fraction/shapes/table) yang sepadan dengan nombor dalam soalan; pelbagaikan jenis rajah antara soalan." } : {}) })) },
      // Official format 2025 (Lembaga Peperiksaan): Bahagian B = 2 linked structured questions, 24 marks (12 each).
      { id: "B", minutes: 30, items: many(2, { kind: "struktur", marks: 12, parts: [2, 3, 3, 4], hint: "Soalan berstruktur berkait: bahagian (a)–(d) menggunakan situasi/data yang sama, dari mudah ke sukar (KBAT di bahagian akhir). Letak 'diagram' vektor (jadual data, carta bar, bentuk bergrid, jam atau garis nombor) pada soalan atau bahagian; semua nombor mesti sepadan dengan rajah." }) },
    ],
  },
  sejarah: {
    id: "sejarah-uasa-sample-2026", subject: "sejarah", paper: 1, code: "019", years: T46, totalMarks: 50, durationMinutes: 75, bilingual: false,
    language: "ms", tahap: 2, continuousNumbering: false, difficultyRatio: [5, 3, 2], verified: false,
    sections: [
      { id: "A", items: many(20, { kind: "objektif", marks: 1 }) },
      { id: "B", items: many(4, { kind: "struktur", marks: 5, parts: [1, 1, 1, 2] }) },
      { id: "C", items: many(2, { kind: "struktur", marks: 5, parts: [1, 2, 2] }) },
    ],
  },
  "sains-t13": contentT13("sains", "018"),
  "matematik-t13": contentT13("matematik", "015"),
  "bahasa_melayu-k1-t13": langPaper1("bahasa_melayu", "ms", "011", ["2."]),
  "bahasa_melayu-k2-t13": langPaper2("bahasa_melayu", "ms", "012", ["3."]),
  "bahasa_inggeris-k1-t13": langPaper1("bahasa_inggeris", "en", "013", ["3."]),
  "bahasa_inggeris-k2-t13": langPaper2("bahasa_inggeris", "en", "014", ["4."]),
  // Tahun 4–6 language papers follow the official Format Instrumen UASA Tahap Dua 2025 (one 50-mark paper, 1 h 15 min).
  "bahasa_melayu-t46": BM_T46,
  "bahasa_inggeris-t46": BI_T46,
};

// ── Picture-led papers: Pendidikan Islam, Pendidikan Moral, Bahasa Arab, PSV, RBT ──────────────────────
/**
 * What these subjects' sample papers have in common (Tahun 1–3 especially): picture-based objective items,
 * padanan (join the pairs), tick/cross statements, fill-in with a word bank, and short structured items on a picture
 * or table. Unlike the A/B/C-only 'umum' paper they are drawn from the icon set, so a Tahun 1 pupil reads pictures.
 */
const PICTURE_ICONS = "GAMBAR: untuk benda/situasi sebenar tulis 'prompt' (penerangan ringkas dalam bahasa Inggeris, 6–25 patah perkataan, satu objek atau adegan yang jelas, tiada teks/huruf/nombor dalam gambar) — sistem melukis lukisan pensel hitam putih. Semua ilustrasi termasuk warna, garisan, tekstur, bentuk dan pose solat MESTI menggunakan prompt imej janaan AI, bukan ikon vektor. Jangan lukis Allah, nabi atau malaikat.";
const PIC_OPT = (g: string): FormatItem => ({
  kind: "objektif", marks: 1, pictureOptions: true,
  hint: `Objektif BERGAMBAR: 'optionPrompts' = 4 penerangan gambar berbeza (satu setiap pilihan A–D; jangan gunakan optionIcons) dan 'options' = 4 penerangan teks ringkas bagi gambar itu (tidak dicetak, untuk skema). stemMs cth 'Pilih gambar yang menunjukkan …' atau 'Manakah gambar …?'. ${g} ${PICTURE_ICONS}`,
});
const PIC_STEM = (g: string): FormatItem => ({
  kind: "objektif", marks: 1, visual: true,
  hint: `Objektif dengan GAMBAR RANGSANGAN: letak 'diagram' jenis scene {prompt} (satu gambar adegan) atau picture (1–4 gambar, label pendek jika perlu) dan 4 pilihan teks. ${g} ${PICTURE_ICONS}`,
});
const TXT_OBJ = (g: string): FormatItem => ({ kind: "objektif", marks: 1, hint: `Objektif teks pendek (pilihan pendek, satu jawapan betul). ${g}` });
const PADAN = (n: number, g: string): FormatItem => ({
  kind: "padan", marks: n,
  hint: `Padanan: stemMs 'Padankan … dengan …. Lukis garisan.'; 'pairs' tepat ${n} pasangan [{left:{prompt|icon|text}, right:{prompt|icon|text}}] — pasangan yang BETUL (sistem akan mengacau lajur kanan). Kiri biasanya gambar (icon), kanan perkataan (text) atau sebaliknya. ${g} ${PICTURE_ICONS}`,
});
const TANDA = (n: number, g: string): FormatItem => ({
  kind: "tanda", marks: n,
  hint: `Tanda (✓) betul / (×) salah: stemMs 'Tandakan (✓) bagi pernyataan yang betul dan (×) bagi pernyataan yang salah.'; 'statements' tepat ${n} [{text, prompt?|icon?, correct:true|false}] — campuran betul dan salah; gambar pilihan (tindakan/situasi). ${g} ${PICTURE_ICONS}`,
});
const LENGKAP = (n: number, g: string): FormatItem => ({
  kind: "isian", marks: n, blanks: n, blankStyle: "alpha", wordBank: true,
  hint: `Lengkapkan ayat dengan perkataan dari kotak (wordBank): 'passage' ${n} ayat pendek berasingan dengan penanda (a)…; 'wordBank' semua jawapan (+1 pengganggu); stemMs 'Pilih perkataan yang betul untuk melengkapkan ayat.' ${g}`,
});
const STRUKTUR_PIC = (marks: number, parts: number[], g: string): FormatItem => ({
  kind: "struktur", marks, parts, visual: true,
  hint: `Struktur BERGAMBAR: letak 'diagram' jenis scene/picture pada soalan atau bahagian (cth item {prompt, ask:true, label:<jawapan>} untuk 'Labelkan/Namakan gambar' — kotak jawapan dicetak sendiri) atau jenis frame untuk 'Lukis/Warnakan di ruang'. Bahagian (a),(b)… mudah dahulu, kemudian aplikasi. ${g} ${PICTURE_ICONS}`,
});

type SubjectGuide = { t13: string; t46: string; code: string };
const VISUAL_SUBJECTS: Record<string, SubjectGuide> = {
  pendidikan_islam: {
    code: "041",
    t13: "Pendidikan Islam T1–3: ibadah (wuduk, solat — guna ikon solat_*), akidah, adab, sirah, amalan harian. Jawi/Al-Quran: guna HANYA huruf/kata/ayat yang tertera dalam huraian SP yang diberi (salin tepat dari situ, jangan reka ayat Al-Quran atau doa); jika tiada, soal dalam Bahasa Melayu bertulisan Jawi sahaja.",
    t46: "Pendidikan Islam T4–6: akidah, ibadah, sirah, akhlak, adab, Jawi. Teks Arab/Jawi (ayat Al-Quran, doa, kata Jawi) hanya yang tertera dalam huraian SP yang diberi — salin tepat, jangan reka. Soalan struktur boleh berasaskan situasi/gambar.",
  },
  pendidikan_moral: {
    code: "042",
    t13: "Pendidikan Moral T1–3: nilai murni dalam situasi harian (rumah, sekolah, kejiranan); guna ikon muka_gembira/sedih/marah, budak_*, murid_*, ibu_bapa_anak, berkongsi, bersalam, beratur, tong_sampah… Soalan pilih tindakan yang betul, padan nilai dengan situasi, tanda betul/salah.",
    t46: "Pendidikan Moral T4–6: nilai dan peraturan dalam situasi nyata; struktur: nyatakan nilai, beri sebab, cadang tindakan. Konteks Malaysia, sesuai umur.",
  },
  bahasa_arab: {
    code: "043",
    t13: "Bahasa Arab T1–3: KOSA KATA asas (keluarga, sekolah, badan, alam, makanan), huruf hijaiyah, ayat mudah. Tulis teks Arab dengan baris (harakat) penuh dalam options/words/passage/pairs/label; arahan soalan JUGA dalam bahasa Arab (kertas sepenuhnya Arab). Gunakan HANYA kosa kata dari huraian SP yang diberi. Padan gambar↔perkataan Arab; objektif bergambar pilih perkataan Arab yang betul.",
    t46: "Bahasa Arab T4–6: kosa kata dan ayat dalam tema DSKP, bacaan pendek, pemahaman mudah. Teks Arab dengan baris; arahan juga dalam bahasa Arab; gunakan hanya kosa kata/struktur dari huraian SP yang diberi.",
  },
  pendidikan_seni_visual: {
    code: "044",
    t13: "Pendidikan Seni Visual T1–3: unsur seni (garisan, warna, bentuk, rupa, tekstur), warna primer/sekunder, alatan dan bahan seni, hasil seni. Guna ikon colour:merah/biru/kuning…, line:zigzag/gelombang…, tex:kasar/licin…, bulatan/segi_*, berus, palet, krayon, gunting, gam. Soalan 'namakan garisan/warna/bentuk', 'warnakan', 'lukis' (diagram frame).",
    t46: "Pendidikan Seni Visual T4–6: unsur dan prinsip seni, teknik, media, apresiasi karya; guna gambar (ikon) dan ruang lukisan (frame) bila sesuai.",
  },
  reka_bentuk_teknologi: {
    code: "045",
    t13: "Reka Bentuk dan Teknologi: bahan, alatan tangan, keselamatan, reka bentuk produk, mekanisme ringkas; guna ikon tukul, gergaji, pemutar_skru, gunting, gam, kertas, tali, mentol, roda_gigi, bateri, botol, tong_kitar_semula.",
    t46: "Reka Bentuk dan Teknologi T4–6: proses reka bentuk (kenal pasti masalah, idea, lukisan, binaan, ujian), alatan dan keselamatan bengkel, bahan, mekanisme, elektronik asas, pertanian/makanan ikut DSKP; guna ikon alatan dan rajah jadual.",
  },
};

function visualPaper(subject: string, tahap: 1 | 2): UasaFormat {
  const g = VISUAL_SUBJECTS[subject]!;
  const years = tahap === 1 ? T13 : T46;
  const guide = tahap === 1 ? g.t13 : g.t46;
  const sections: FormatSection[] = tahap === 1
    ? [
        { id: "A", items: [...many(6, PIC_OPT(guide)), ...many(3, PIC_STEM(guide)), ...many(3, TXT_OBJ(guide))] },
        { id: "B", items: [PADAN(4, guide), TANDA(4, guide), LENGKAP(4, guide)] },
        { id: "C", items: many(4, STRUKTUR_PIC(4, [2, 2], guide)) },
      ]
    : [
        { id: "A", items: [...many(5, PIC_OPT(guide)), ...many(5, PIC_STEM(guide)), ...many(5, TXT_OBJ(guide))] },
        { id: "B", items: [PADAN(5, guide), TANDA(5, guide)] },
        { id: "C", items: many(5, STRUKTUR_PIC(5, [1, 2, 2], guide)) },
      ];
  const total = sections.reduce((a, sec) => a + sectionMarks(sec), 0);
  return {
    id: `${subject}-t${years[0]}${years[2]}-visual-2026`, subject, paper: 1, code: g.code, years, totalMarks: total,
    durationMinutes: tahap === 1 ? 60 : 75, bilingual: false, language: "ms", tahap, continuousNumbering: false,
    difficultyRatio: [5, 3, 2], sections, verified: false,
  };
}
for (const subj of Object.keys(VISUAL_SUBJECTS)) {
  (UASA_FORMATS as Record<string, UasaFormat>)[`${subj}-t13-visual`] = visualPaper(subj, 1);
  (UASA_FORMATS as Record<string, UasaFormat>)[`${subj}-t46-visual`] = visualPaper(subj, 2);
}

// ── Core subjects that also carry AI pencil illustrations (Sains, Sejarah, BM, BI) ─────────────────────
const PICTURE_CORE: Record<string, SubjectGuide> = {
  sains: {
    code: "018",
    t13: "Sains T1–3: pancaindera, haiwan, tumbuhan, bahan, daya, bumi — gambar organisma, radas ringkas, situasi harian; soalan mesti merujuk apa yang BENAR-BENAR tergambar (bahagian, bilangan, kedudukan, perbandingan).",
    t46: "Sains T4–6: gambar susunan radas, organisma, kitaran/proses, alam sekitar, binaan ringkas; soalan merujuk pemerhatian pada gambar (label A/B/C dicetak oleh sistem), kemudian jadual data untuk kemahiran proses sains.",
  },
  sejarah: {
    code: "019",
    t13: "Sejarah: tempat, bangunan, alat dan pakaian bersejarah Malaysia dalam gambar; elakkan wajah tokoh sebenar yang mudah dikenali — guna bangunan, artifak, peta, adegan umum.",
    t46: "Sejarah T4–6: sumber visual (bangunan bersejarah, artifak, alat, pakaian, adegan zaman dahulu, lakaran peta); soalan struktur menafsir gambar sumber (nyatakan, kenal pasti, jelaskan kepentingan). JANGAN lukis wajah tokoh sebenar atau nabi; guna bangunan, artifak dan adegan umum.",
  },
  bahasa_melayu: {
    code: "011",
    t13: "Bahasa Melayu: gambar situasi harian/aktiviti/benda untuk kosa kata, ayat dan tatabahasa; pilih perkataan atau ayat yang sesuai dengan gambar.",
    t46: "Bahasa Melayu: gambar situasi/aktiviti/poster sebagai rangsangan untuk kosa kata, imbuhan, peribahasa dan pemahaman; ayat mesti sepadan dengan gambar.",
  },
  bahasa_inggeris: {
    code: "013",
    t13: "English: pictures of everyday objects, actions and places for vocabulary, sentences and grammar; the answer must be identifiable from the picture.",
    t46: "English: pictures of scenes/activities as stimulus for vocabulary, grammar and comprehension; text must match what the picture really shows.",
  },
};
const pictureGuide = (subject: string, year: number): string => (year <= 3 ? PICTURE_CORE[subject]!.t13 : PICTURE_CORE[subject]!.t46);

/** Turn a plain format into the real UASA look: picture options, picture stimulus and picture-based struktur. */
export function pictureize(f: UasaFormat): UasaFormat {
  const g = pictureGuide(f.subject, f.years[0] ?? 4);
  const sections = f.sections.map((sec) => {
    const items = sectionItems(sec);
    const obj = items.filter((i) => i.kind === "objektif" && !i.visual && !i.pictureOptions);
    const nOpt = Math.round(obj.length * (f.tahap === 1 ? 0.3 : 0.2)), nStem = Math.round(obj.length * 0.3);
    let k = 0;
    const out = items.map((it): FormatItem => {
      if (it.kind === "objektif" && !it.visual && !it.pictureOptions) {
        const i = k++;
        if (i < nOpt) return { ...PIC_OPT(g), marks: it.marks };
        if (i < nOpt + nStem) return { ...PIC_STEM(g), marks: it.marks };
        return { ...it, hint: `${(it.hint ?? "").replace(OBJ_PIC, "").trim()} Objektif teks pendek (pilihan pendek, satu jawapan betul). ${g}`.trim() };
      }
      if (it.kind === "struktur" && !it.visual && !it.passage && f.subject !== "bahasa_melayu" && f.subject !== "bahasa_inggeris")
        return { ...STRUKTUR_PIC(it.marks, it.parts ?? [it.marks], g), ...(it.hint ? { hint: `${STRUKTUR_PIC(it.marks, it.parts ?? [it.marks], g).hint} ${it.hint}` } : {}) };
      return it;
    });
    return { ...sec, items: out };
  });
  return { ...f, sections };
}
for (const [id, f] of Object.entries(UASA_FORMATS)) {
  if (f.subject in PICTURE_CORE) (UASA_FORMATS as Record<string, UasaFormat>)[id] = pictureize(f);
}

/** Subjects whose papers are picture-led (AI pencil illustrations): the five picture-led subjects plus Sains, Sejarah, BM, BI. */
export const isVisualSubject = (subject: string) => subject in VISUAL_SUBJECTS || subject in PICTURE_CORE;
/** Only the five picture-led subjects use the icon catalogue and the strict raster-only rules. */
export const isStrictVisualSubject = (subject: string) => subject in VISUAL_SUBJECTS;
export const visualSubjectGuide = (subject: string, year: number): string | null => (PICTURE_CORE[subject] ? pictureGuide(subject, year) : VISUAL_SUBJECTS[subject] ? (year <= 3 ? VISUAL_SUBJECTS[subject]!.t13 : VISUAL_SUBJECTS[subject]!.t46) : null);

/** Which right-hand position each left item is printed against in a padan question: deterministic, never in the original order. */
export function matchOrder(n: number, seed: number): number[] {
  if (n < 2) return Array.from({ length: n }, (_, i) => i);
  const rot = 1 + (Math.abs(seed) % (n - 1));
  const order = Array.from({ length: n }, (_, i) => (i + rot) % n);
  if (n >= 4) {
    const a = Math.abs(seed) % n, b = (a + 2) % n;
    const t = order.slice();
    [t[a], t[b]] = [t[b]!, t[a]!];
    if (t.every((v, i) => v !== i)) return t;
  }
  return order;
}

/**
 * Any other subject (Pendidikan Islam, Pendidikan Moral, RBT, Seni, Muzik, PJK, Bahasa Arab…): a
 * general paper in the shape of the samples — objektif + struktur (Tahun 1–3) or A/B/C (Tahun 4–6).
 */
export function genericUasaFormat(subject: string, year: number): UasaFormat | null {
  if (!Number.isInteger(year) || year < 1 || year > 6) return null;
  if (year <= 3) return { ...contentT13("sains", "000"), id: "umum-t13-sample-2026", subject, sections: [
    { id: "A", items: many(20, { kind: "objektif", marks: 1, hint: OBJ_PIC }) },
    { id: "B", items: many(4, { kind: "struktur", marks: 5, parts: [2, 3], hint: "Struktur ringkas: nyatakan, padankan, tanda (/), lengkapkan jadual atau situasi." }) },
  ] };
  return { ...UASA_FORMATS.sejarah!, id: "umum-t46-sample-2026", subject, code: "000" };
}

/**
 * "Latihan ikut DSKP" in the same paper style: Bahagian A objektif, Bahagian B struktur (with diagrams),
 * sized from the question count the user picked. Works for any subject, Tahun 1–6 and Tingkatan 1–5.
 */
export function practiceUasaFormat(o: { subject: string; level: "rendah" | "menengah"; year: number; questionCount: number; language: "ms" | "en" | "dlp"; difficultyRatio?: [number, number, number] }): UasaFormat {
  const n = Math.max(5, Math.min(30, Math.round(o.questionCount)));
  const tahap: 1 | 2 = o.level === "rendah" && o.year <= 3 ? 1 : 2;
  const lang: "ms" | "en" = o.language === "en" || o.subject === "bahasa_inggeris" ? "en" : "ms";
  const langSubject = o.subject === "bahasa_melayu" || o.subject === "bahasa_inggeris";
  const nStr = Math.max(1, Math.min(6, Math.round(n * 0.3)));
  const nObj = n - nStr;
  const str: FormatItem[] = Array.from({ length: nStr }, (_, i) =>
    langSubject && i === 0
      ? COMPREHENSION(lang)
      : tahap === 1
        ? { kind: "struktur" as const, marks: 5, parts: [2, 3] }
        : { kind: "struktur" as const, marks: 6, parts: [1, 2, 3], hint: "Struktur dengan situasi/data; guna rajah atau jadual bila sesuai (Matematik: rajah wajib bila topik geometri, masa, pecahan atau data)." });
  const sections: FormatSection[] = [
    { id: "A", items: many(nObj, { kind: "objektif", marks: 1, hint: OBJ_PIC }) },
    { id: "B", items: str },
  ];
  // Picture-led subjects (Islam, Moral, Arab, PSV, RBT) keep their real paper style in practice mode too:
  // picture options, picture stimulus, padan / tanda / lengkap and picture-based struktur, scaled to the question count.
  if (VISUAL_SUBJECTS[o.subject] && o.level === "rendah") {
    const g = tahap === 1 ? VISUAL_SUBJECTS[o.subject]!.t13 : VISUAL_SUBJECTS[o.subject]!.t46;
    const vStr = Math.max(1, Math.round(n * 0.25));
    const mix = n >= 10 ? (tahap === 1 ? [PADAN(4, g), TANDA(4, g), LENGKAP(4, g)] : [PADAN(5, g), TANDA(5, g)]).slice(0, Math.max(1, Math.round(n * 0.15))) : [];
    const vObj = n - vStr - mix.length;
    const nOpt = Math.round(vObj * (tahap === 1 ? 0.45 : 0.35)), nStem = Math.round(vObj * 0.3);
    const objItems = [...many(nOpt, PIC_OPT(g)), ...many(nStem, PIC_STEM(g)), ...many(vObj - nOpt - nStem, TXT_OBJ(g))];
    const vStruct = many(vStr, STRUKTUR_PIC(tahap === 1 ? 4 : 5, tahap === 1 ? [2, 2] : [1, 2, 2], g));
    sections.length = 0;
    sections.push({ id: "A", items: objItems });
    if (mix.length) sections.push({ id: "B", items: mix });
    sections.push({ id: mix.length ? "C" : "B", items: vStruct });
  }
  const total = sections.reduce((a, s) => a + sectionMarks(s), 0);
  const built: UasaFormat = {
    id: "dskp-practice-2026", subject: o.subject, paper: 1, paperName: lang === "en" ? "Latihan ikut DSKP (DSKP Practice)" : "Latihan ikut DSKP", code: "000",
    years: [o.year], totalMarks: total, durationMinutes: Math.max(30, Math.ceil((nObj * 1.5 + nStr * 7 + (VISUAL_SUBJECTS[o.subject] ? 10 : 0)) / 15) * 15),
    bilingual: o.language === "dlp", language: lang, tahap, continuousNumbering: false,
    difficultyRatio: o.difficultyRatio ?? [5, 3, 2], sections, verified: false,
  };
  return o.subject in PICTURE_CORE && o.level === "rendah" ? pictureize(built) : built;
}

/** Every UASA paper available for a subject and year (language subjects have Kertas 1 and 2). */
export function uasaPapersFor(subject: string, year: number): UasaFormat[] {
  const own = Object.values(UASA_FORMATS).filter((f) => f.subject === subject && f.years.includes(year)).sort((a, b) => a.paper - b.paper);
  if (own.length) return own;
  const g = genericUasaFormat(subject, year);
  return g ? [g] : [];
}

export const uasaFormatFor = (subject: string, year: number, paper = 1): UasaFormat | null => {
  const all = uasaPapersFor(subject, year);
  // A paper that no longer exists (an older screen asking for Kertas 2 of a language that is now one paper) falls back to paper 1.
  return all.find((f) => f.paper === paper) ?? all.find((f) => f.paper === 1) ?? null;
};

/** Standards a paper draws on: the paper's skill area (Kertas 1 reading, Kertas 2 writing) within the chosen units. */
export function standardsForFormat<T extends { spCode: string }>(f: UasaFormat, standards: readonly T[]): T[] {
  if (!f.spPrefixes?.length) return [...standards];
  const hit = standards.filter((s) => f.spPrefixes!.some((p) => s.spCode.replace(/^K/, "").startsWith(p)));
  return hit.length ? hit : [...standards];
}

// ── Blueprint ───────────────────────────────────────────────────────────────
export interface UasaSlot {
  visual?: boolean;
  section: SectionId;
  number: number;
  kind: QuestionKind;
  marks: number;
  parts?: number[];
  blanks?: number;
  blankStyle?: "num" | "alpha";
  wordBank?: boolean;
  stimulus?: "poster" | "panels";
  passage?: boolean;
  pictureOptions?: boolean;
  hint?: string;
  difficulty: Difficulty;
  spCode: string;
  topic: string;
}

export interface UasaBlueprint {
  format: UasaFormat;
  title: string;
  subjectName: string;
  year: number;
  /** "Tahun 2" / "Tingkatan 3" (absent on blueprints stored before Tingkatan support). */
  yearLabel?: string;
  slots: UasaSlot[];
  standards: CurriculumStandard[];
}

const SUBJECT_NAME: Record<string, string> = {
  sains: "Sains", matematik: "Matematik", sejarah: "Sejarah", bahasa_melayu: "Bahasa Melayu", bahasa_inggeris: "Bahasa Inggeris",
  pendidikan_islam: "Pendidikan Islam", pendidikan_moral: "Pendidikan Moral", reka_bentuk_teknologi: "Reka Bentuk dan Teknologi",
  pendidikan_jasmani: "Pendidikan Jasmani", pendidikan_kesihatan: "Pendidikan Kesihatan", pendidikan_seni_visual: "Pendidikan Seni Visual",
  pendidikan_muzik: "Pendidikan Muzik", bahasa_arab: "Bahasa Arab", bahasa_cina: "Bahasa Cina", bahasa_tamil: "Bahasa Tamil",
};
export const uasaSubjectName = (s: string) => SUBJECT_NAME[s] ?? s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

/** Plan every question: section, kind, marks (and part marks), difficulty and the SP it assesses. */
export function buildUasaBlueprint(format: UasaFormat, year: number, standards: readonly CurriculumStandard[], level: "rendah" | "menengah" = "rendah"): UasaBlueprint {
  if (standards.length === 0) throw new RangeError("Tiada Standard Pembelajaran untuk unit yang dipilih");
  // Spread questions across units: interleave SPs unit by unit so every chosen unit is covered.
  const byUnit = new Map<string, CurriculumStandard[]>();
  for (const s of standardsForFormat(format, standards)) {
    const k = s.topic ?? s.skCode;
    byUnit.set(k, [...(byUnit.get(k) ?? []), s]);
  }
  const units = [...byUnit.values()];
  const pool = units.reduce((a, u) => a + u.length, 0);
  const order: CurriculumStandard[] = [];
  for (let i = 0; order.length < pool; i++) for (const u of units) if (u[i]) order.push(u[i]!);

  const total = format.sections.reduce((a, s) => a + sectionItems(s).length, 0);
  const [lo, mid, hi] = expectedDifficultyCounts(format.difficultyRatio, total);
  const diffs: Difficulty[] = [...Array(lo).fill("rendah"), ...Array(mid).fill("sederhana"), ...Array(hi).fill("tinggi")];

  const slots: UasaSlot[] = [];
  let k = 0;
  for (const sec of format.sections) {
    sectionItems(sec).forEach((it, i) => {
      const sp = order[k % order.length]!;
      const number = format.continuousNumbering ? k + 1 : i + 1;
      const { kind, marks, ...extra } = it;
      const parts = kind === "struktur" ? it.parts ?? [marks] : undefined;
      slots.push({ section: sec.id, number, kind, marks, ...extra, ...(parts ? { parts } : {}), difficulty: diffs[k] ?? "sederhana", spCode: sp.spCode, topic: sp.topic ?? sp.skCode });
      k++;
    });
  }
  const used = new Set(slots.map((s) => s.spCode));
  const subjectName = uasaSubjectName(format.subject);
  const paper = format.id === "dskp-practice-2026" ? " (Latihan DSKP)" : uasaPapersFor(format.subject, year).length > 1 ? ` Kertas ${format.paper}` : "";
  const yearLabel = `${level === "menengah" ? "Tingkatan" : "Tahun"} ${year}`;
  return {
    format,
    title: `${subjectName}${paper} ${yearLabel}`,
    subjectName,
    year,
    yearLabel,
    slots,
    standards: standards.filter((s) => used.has(s.spCode)),
  };
}

// ── Questions and validation ────────────────────────────────────────────────
export interface UasaPart {
  label: string;
  stemMs: string;
  stemEn?: string;
  marks: number;
  answer: string;
  check?: string;
  diagram?: Diagram;
}

/** stemMs/options hold the paper's own language (English for Bahasa Inggeris); stemEn is the DLP italics. */
export interface UasaQuestion {
  section: SectionId;
  number: number;
  kind: QuestionKind;
  marks: number;
  difficulty: Difficulty;
  spCode: string;
  stemMs: string;
  stemEn?: string;
  diagram?: Diagram;
  options?: string[];
  optionsEn?: string[];
  answer?: string;
  check?: string;
  parts?: UasaPart[];
  /** susun: tiles in the order printed. */
  words?: string[];
  /** struktur on a passage, or the isian text with its blank markers (1)… / (a)…. */
  passage?: string;
  passageTitle?: string;
  /** isian */
  wordBank?: string[];
  posterTitle?: string;
  kerangka?: string[][];
  blanks?: string[];
  /** objektif: one icon name per option, printed instead of the option text (which then only feeds the scheme). */
  optionIcons?: string[];
  /** objektif: one image description per option (generated illustrations); optionIcons may back them up. */
  optionPrompts?: string[];
  optionImageIds?: (string | null)[];
  /** padan: the correct pairs; the renderer shuffles the right-hand column with matchOrder(). */
  pairs?: { left: MatchItem; right: MatchItem }[];
  /** tanda: statements to mark ✓ or ×. */
  statements?: { text?: string; icon?: string; prompt?: string; imageId?: string; correct: boolean }[];
}

export interface MatchItem { text?: string; icon?: string; prompt?: string; imageId?: string }

/** A numeric answer must match its check; worded answers ("Ya, kerana …") are not machine-checked. */
function checkIssue(where: string, check: string | undefined, answer: string): ValidationIssue | null {
  if (!check || parseNumericAnswer(answer) === null) return null;
  const r = checkArithmetic(check, answer);
  return r.ok ? null : { code: "wrong_answer", message: `${where}: ${r.reason}` };
}

export const blankLabel = (style: "num" | "alpha" | undefined, i: number) => (style === "alpha" ? String.fromCharCode(97 + i) : String(i + 1));
const tokens = (t: string) => t.toLowerCase().replace(/[.,!?;:"“”'‘’()]/g, " ").split(/\s+/).filter(Boolean);
const wordCount = (t: string | undefined) => (t ?? "").split(/\s+/).filter(Boolean).length;

/** Text printed for pupils and the answer scheme, excluding image prompts and internal metadata. */
export function printedQuestionTexts(q: UasaQuestion): string[] {
  const out: string[] = [];
  const keys = new Set(["stemMs", "passage", "passageTitle", "wordBank", "words", "blanks", "options", "posterTitle", "kerangka", "text", "textMs", "label", "headers", "rows", "xLabel", "yLabel", "answer"]);
  const walk = (v: unknown, key = "", parent?: Record<string, unknown>) => {
    if (typeof v === "string") {
      if (keys.has(key) && !(key === "answer" && (parent?.kind === "objektif" || /روومي|رومي/.test(String(parent?.stemMs ?? "")))) && !(key === "label" && parent && "marks" in parent)) out.push(v);
    } else if (Array.isArray(v)) for (const x of v) walk(x, key, parent);
    else if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) walk(x, k, v as Record<string, unknown>);
  };
  walk(q);
  return out;
}

export function validateUasaPaper(bp: UasaBlueprint, qs: readonly UasaQuestion[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const bi = bp.format.bilingual;
  const script = paperScript(bp.format.subject, /^Tingkatan/i.test(bp.yearLabel ?? "") ? "menengah" : "rendah");
  const push = (code: string, message: string) => issues.push({ code, message });
  for (const slot of bp.slots) {
    const w = `Soalan ${slot.section}${slot.number}`;
    const q = qs.find((x) => x.section === slot.section && x.number === slot.number);
    if (!q) { push("missing", `${w} tiada`); continue; }
    const stemOptional = slot.kind === "susun";
    if (!stemOptional && !str(q.stemMs)) push("empty_stem", `${w}: soalan kosong`);
    if (script) {
      for (const text of printedQuestionTexts(q)) {
        const problem = scriptProblem(script, text);
        if (problem) { push("script", `${w}: ${problem} (${text.slice(0, 65)})`); break; }
      }
    }
    // Pure computations ("43 027 × (40 − 34) =") need no translation.
    if (bi && !str(q.stemEn) && /[A-Za-z]{3,}/.test(q.stemMs ?? "")) push("missing_en", `${w}: stemEn (English) tiada`);
    if (q.diagram !== undefined) { const p = diagramProblem(q.diagram); if (p) push("diagram", `${w}: ${p}`); }
    if (slot.kind === "objektif") {
      const o = q.options ?? [];
      if (o.length !== 4 || !o.every(str)) push("options", `${w}: perlu 4 pilihan`);
      else if (new Set(o.map((x) => x.trim())).size !== 4) push("options_duplicate", `${w}: pilihan berulang`);
      if (bi && (q.optionsEn?.length !== 4 || !q.optionsEn.every(str))) push("options_en", `${w}: optionsEn perlu 4 pilihan`);
      if (slot.pictureOptions || q.optionIcons !== undefined || q.optionPrompts !== undefined) {
        const ic = q.optionIcons ?? [], pr = q.optionPrompts ?? [];
        const viaPrompt = pr.length > 0;
        const items = viaPrompt ? pr : ic;
        if (items.length !== 4 || !items.every((x) => (viaPrompt ? promptProblem(x, true) === null : isIcon(x)))) push("option_icons", `${w}: perlu 4 gambar — 'optionPrompts' (penerangan) atau 'optionIcons' (nama ikon yang dikenali)`);
        else if (new Set(items.map((x) => x.trim().toLowerCase())).size !== 4) push("option_icons", `${w}: gambar pilihan berulang`);
        if (viaPrompt && ic.length && ic.length !== 4) push("option_icons", `${w}: optionIcons (sandaran) perlu 4 jika diberi`);
        for (const x of pr) { const pp = promptProblem(x); if (pp) push("image_prompt", `${w}: ${pp}`); }
      }
      const idx = optionIndex(q.answer ?? "");
      if (idx < 0) push("objective_answer", `${w}: jawapan mesti A–D`);
      else { const i = checkIssue(w, q.check, o[idx] ?? ""); if (i) issues.push(i); }
    } else if (slot.kind === "jawapan") {
      if (!str(q.answer)) push("empty_answer", `${w}: tiada jawapan`);
      else { const i = checkIssue(w, q.check, q.answer); if (i) issues.push(i); }
    } else if (slot.kind === "susun") {
      const ws = q.words ?? [];
      if (ws.length < 3 || ws.length > 8 || !ws.every(str)) push("words", `${w}: 'words' perlu 3–8 jubin`);
      else if (!str(q.answer)) push("empty_answer", `${w}: tiada ayat jawapan`);
      else {
        const a = tokens(q.answer).sort().join(" "), t = ws.flatMap(tokens).sort().join(" ");
        if (a !== t) push("words_mismatch", `${w}: ayat jawapan mesti guna semua jubin tepat sekali (jubin: ${ws.join(" | ")})`);
        else if (ws.flatMap(tokens).join(" ") === tokens(q.answer).join(" ")) push("words_order", `${w}: jubin masih dalam susunan betul — campurkan susunannya`);
      }
    } else if (slot.kind === "padan") {
      const pr = q.pairs ?? [];
      const side = (m: MatchItem | undefined) => !!m && (str(m.text) || str(m.prompt) || (m.icon !== undefined && isIcon(m.icon))) && (m.icon === undefined || isIcon(m.icon)) && promptProblem(m.prompt) === null;
      const key = (m: MatchItem) => (m.prompt ? `p:${m.prompt.trim().toLowerCase()}` : m.icon ? `i:${m.icon.trim().toLowerCase()}` : `t:${(m.text ?? "").trim().toLowerCase()}`);
      if (pr.length !== slot.marks) push("pairs", `${w}: 'pairs' perlu tepat ${slot.marks} pasangan`);
      else if (!pr.every((x) => side(x?.left) && side(x?.right))) push("pairs", `${w}: setiap pasangan perlu left dan right dengan text atau icon yang dikenali`);
      else {
        if (new Set(pr.map((x) => key(x.left))).size !== pr.length) push("pairs", `${w}: item kiri berulang`);
        if (new Set(pr.map((x) => key(x.right))).size !== pr.length) push("pairs", `${w}: item kanan berulang`);
      }
    } else if (slot.kind === "tanda") {
      const st = q.statements ?? [];
      if (st.length !== slot.marks) push("statements", `${w}: 'statements' perlu tepat ${slot.marks} pernyataan`);
      else {
        if (!st.every((x) => x && typeof x.correct === "boolean" && (str(x.text) || str(x.prompt) || (x.icon !== undefined && isIcon(x.icon))) && (x.icon === undefined || isIcon(x.icon)) && promptProblem(x.prompt) === null)) push("statements", `${w}: setiap pernyataan perlu text/icon yang dikenali dan correct (true/false)`);
        else if (st.length >= 3 && (st.every((x) => x.correct) || st.every((x) => !x.correct))) push("statements", `${w}: campurkan pernyataan betul dan salah`);
      }
    } else if (slot.kind === "isian") {
      const n = slot.blanks ?? 0;
      const b = q.blanks ?? [];
      if (b.length !== n || !b.every(str)) push("blanks", `${w}: 'blanks' perlu tepat ${n} jawapan`);
      if (!str(q.passage)) push("passage", `${w}: 'passage' tiada`);
      else {
        for (let i = 0; i < n; i++) {
          const m = `(${blankLabel(slot.blankStyle, i)})`;
          const c = q.passage.split(m).length - 1;
          if (c !== 1) push("blank_marker", `${w}: penanda ${m} mesti muncul tepat sekali dalam passage (jumpa ${c})`);
        }
      }
      if (slot.wordBank) {
        const bank = (q.wordBank ?? []).map((x) => x.trim().toLowerCase());
        if (bank.length < n || bank.length > n + 3) push("word_bank", `${w}: wordBank perlu ${n}–${n + 3} perkataan`);
        b.forEach((x, i) => { if (str(x) && !bank.includes(x.trim().toLowerCase())) push("word_bank", `${w}: jawapan (${blankLabel(slot.blankStyle, i)}) '${x}' tiada dalam wordBank`); });
      }
      if (slot.stimulus) {
        const k = q.kerangka ?? [];
        const panels = slot.stimulus === "poster" ? 1 : 4;
        if (k.length !== panels || !k.every((pn) => Array.isArray(pn) && pn.length >= 1 && pn.length <= 6 && pn.every(str)))
          push("kerangka", `${w}: 'kerangka' perlu ${panels} panel, setiap panel 1–6 isi`);
        if (slot.stimulus === "poster" && !str(q.posterTitle)) push("kerangka", `${w}: 'posterTitle' tiada`);
      }
    } else {
      if (slot.passage && wordCount(q.passage) < 40) push("passage", `${w}: 'passage' (petikan) perlu sekurang-kurangnya 40 patah perkataan`);
      const parts = q.parts ?? [];
      const want = slot.parts ?? [slot.marks];
      if (parts.length !== want.length) push("parts", `${w}: perlu ${want.length} bahagian kecil (a), (b)…`);
      const sum = parts.reduce((a, p) => a + (Number(p?.marks) || 0), 0);
      if (sum !== slot.marks) push("part_marks", `${w}: jumlah markah bahagian ${sum}, dijangka ${slot.marks}`);
      parts.forEach((p, i) => {
        const pw = `${w}(${String.fromCharCode(97 + i)})`;
        if (!str(p?.stemMs)) push("empty_stem", `${pw}: soalan kosong`);
        if (bi && !str(p?.stemEn)) push("missing_en", `${pw}: stemEn tiada`);
        if (!str(p?.answer)) push("empty_answer", `${pw}: tiada jawapan`);
        else { const c = checkIssue(pw, p.check, p.answer); if (c) issues.push(c); }
        if (p?.diagram !== undefined) { const d = diagramProblem(p.diagram); if (d) push("diagram", `${pw}: ${d}`); }
      });
    }
  }
  return issues;
}

/** Normalise model output: strings everywhere, labels a, b, c…, marks from the blueprint. */
export function normaliseUasaQuestions(bp: UasaBlueprint, raw: unknown): UasaQuestion[] {
  const s = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  const strs = (v: unknown) => (Array.isArray(v) ? v.map((x) => s(x && typeof x === "object" && "prompt" in x ? (x as { prompt: unknown }).prompt : x)) : undefined);
  const matchItem = (v: unknown): MatchItem => {
    if (typeof v === "string") return /^[a-z_]+(:[a-z ]+)?$/.test(v.trim()) && isIcon(v.trim()) ? { icon: v.trim() } : { text: v };
    const m = (v ?? {}) as Record<string, unknown>;
    return { ...(str(m.text) ? { text: s(m.text) } : {}), ...(m.icon ? { icon: s(m.icon).trim() } : {}), ...(str(m.prompt) ? { prompt: s(m.prompt).trim() } : {}) };
  };
  const truthy = (v: unknown) => v === true || (typeof v === "string" && /^(true|betul|benar|ya|yes|✓|√|\/)$/i.test(v.trim()));
  const list = Array.isArray((raw as { questions?: unknown })?.questions) ? (raw as { questions: unknown[] }).questions : [];
  return list.map((r) => {
    const x = (r ?? {}) as Record<string, unknown>;
    const section = s(x.section).toUpperCase() as SectionId;
    const number = Number(x.number);
    const slot = bp.slots.find((z) => z.section === section && z.number === number);
    const kerangka = Array.isArray(x.kerangka)
      ? (x.kerangka as unknown[]).map((pn) => (Array.isArray(pn) ? pn.map(s) : [s(pn)]))
      : undefined;
    const q: UasaQuestion = {
      section, number, kind: slot?.kind ?? "jawapan", marks: slot?.marks ?? Number(x.marks) ?? 0,
      difficulty: slot?.difficulty ?? "sederhana", spCode: slot?.spCode ?? s(x.spCode),
      stemMs: s(x.stemMs), ...(x.stemEn ? { stemEn: s(x.stemEn) } : {}),
      ...(x.diagram ? { diagram: x.diagram as Diagram } : {}),
      ...(Array.isArray(x.options) ? { options: strs(x.options)! } : {}),
      ...(Array.isArray(x.optionsEn) ? { optionsEn: strs(x.optionsEn)! } : {}),
      ...(x.answer !== undefined ? { answer: s(x.answer) } : {}),
      ...(x.check ? { check: s(x.check) } : {}),
      ...(Array.isArray(x.words) ? { words: strs(x.words)! } : {}),
      ...(x.passage ? { passage: s(x.passage) } : {}),
      ...(x.passageTitle ? { passageTitle: s(x.passageTitle) } : {}),
      ...(Array.isArray(x.wordBank) ? { wordBank: strs(x.wordBank)! } : {}),
      ...(x.posterTitle ? { posterTitle: s(x.posterTitle) } : {}),
      ...(kerangka ? { kerangka } : {}),
      ...(Array.isArray(x.blanks) ? { blanks: strs(x.blanks)! } : {}),
      ...(Array.isArray(x.optionIcons) ? { optionIcons: strs(x.optionIcons)!.map((v) => v.trim()) } : {}),
      ...(Array.isArray(x.optionPrompts) ? { optionPrompts: strs(x.optionPrompts)!.map((v) => v.trim()) } : {}),
      ...(Array.isArray(x.pairs) ? { pairs: (x.pairs as Record<string, unknown>[]).map((p) => ({ left: matchItem(p?.left), right: matchItem(p?.right) })) } : {}),
      ...(Array.isArray(x.statements) ? { statements: (x.statements as Record<string, unknown>[]).map((p) => ({ ...(str(p?.text) ? { text: s(p.text) } : {}), ...(p?.icon ? { icon: s(p.icon).trim() } : {}), ...(str(p?.prompt) ? { prompt: s(p.prompt).trim() } : {}), correct: truthy(p?.correct) })) } : {}),
    };
    if (Array.isArray(x.parts)) {
      q.parts = (x.parts as Record<string, unknown>[]).map((p, i) => ({
        label: String.fromCharCode(97 + i),
        stemMs: s(p?.stemMs), ...(p?.stemEn ? { stemEn: s(p.stemEn) } : {}),
        marks: Number(p?.marks) || 0, answer: s(p?.answer),
        ...(p?.check ? { check: s(p.check) } : {}),
        ...(p?.diagram ? { diagram: p.diagram as Diagram } : {}),
      }));
    }
    return q;
  });
}


/** One generated picture a paper needs: its description and the object that will carry the stored image id. */
export interface ImageRef { prompt: string; holder: { imageId?: string }; /** The question text this picture belongs to; used to check the picture really matches it. */ context?: string }

/**
 * Every illustration the paper asks for, in print order (duplicates are separate refs: each paper's pictures are
 * generated fresh). `optionPrompts` have no holder object, so they are exposed through a proxy writing optionImageIds.
 */
export function imageRefs(qs: readonly UasaQuestion[]): ImageRef[] {
  const out: ImageRef[] = [];
  let ctx = "";
  const diagram = (d: Diagram | undefined) => {
    if (!d) return;
    if (d.type === "scene" && d.prompt) out.push({ prompt: d.prompt, holder: d, context: ctx });
    if (d.type === "picture" && Array.isArray(d.items)) for (const it of d.items) if (it?.prompt) out.push({ prompt: it.prompt, holder: it, context: ctx });
  };
  for (const q of qs) {
    ctx = String(q.stemMs ?? "").slice(0, 300);
    diagram(q.diagram);
    q.optionPrompts?.forEach((p, i) => {
      q.optionImageIds ??= [null, null, null, null];
      out.push({ prompt: p, context: `${ctx} | pilihan ${"ABCD"[i]}: ${String(q.options?.[i] ?? "").slice(0, 120)}`, holder: { get imageId() { return q.optionImageIds![i] ?? undefined; }, set imageId(v) { q.optionImageIds![i] = v ?? null; } } });
    });
    for (const pr of q.pairs ?? []) for (const m of [pr.left, pr.right]) if (m.prompt) out.push({ prompt: m.prompt, holder: m, context: `${ctx} | ${String(pr.left.text ?? pr.right.text ?? "").slice(0, 100)}` });
    for (const st of q.statements ?? []) if (st.prompt) out.push({ prompt: st.prompt, holder: st, context: String(st.text ?? "").slice(0, 200) });
    for (const p of q.parts ?? []) diagram(p.diagram);
  }
  return out;
}

/** All stored image ids a paper refers to (for the client to download before rendering). */
export function imageIds(qs: readonly UasaQuestion[]): string[] {
  const ids = new Set<string>();
  for (const r of imageRefs(qs)) if (r.holder.imageId) ids.add(r.holder.imageId);
  return [...ids];
}

